/**
 * How much SES engagement tracking one email template asks for.
 *
 *  - `full`   — open pixel AND rewritten links. The default, and what every
 *               template stored before 2026-09 means.
 *  - `clicks` — rewritten links, NO open pixel.
 *  - `none`   — neither (the old `trackingDisabled: true`).
 *
 * Lower-case on the wire in all three repos; main-backend `EmailTrackingMode`
 * is the authority, main-consumer `lib/tracking-mode.ts` is the twin of the
 * resolver below.
 */
export type TrackingMode = "full" | "clicks" | "none";

export const TRACKING_MODES: readonly TrackingMode[] = ["full", "clicks", "none"];

export function isTrackingMode(value: unknown): value is TrackingMode {
  return typeof value === "string" && (TRACKING_MODES as readonly string[]).includes(value);
}

/**
 * The mode a template row asks for, reading BOTH generations of the field —
 * the same precedence main-consumer sends with, so the settings dialog can
 * never show a mode the next mail will not use.
 *
 * `trackingDisabled === true` wins: the backend keeps the two fields in
 * agreement on every write, so on a healthy row this changes nothing, and on
 * the unhealthy one (a backend instance older than `tracking` wrote the boolean
 * alone) it resolves towards the opt-out, which is what the consumer does too.
 * Anything unknown is `full`, today's behaviour.
 */
export function resolveTrackingMode(
  row: { tracking?: unknown; trackingDisabled?: unknown } | null | undefined,
): TrackingMode {
  if (row?.trackingDisabled === true) return "none";
  return isTrackingMode(row?.tracking) ? row.tracking : "full";
}

/**
 * May the settings dialog OFFER `clicks`?
 *
 * `clicks` exists only as a per-message SES override, which main-consumer sends
 * when its own `SES_TRACKING_OVERRIDES_ENABLED` is on. Until then it degrades
 * `clicks` to `full` — the mail ships WITH a pixel — and the cockpit has no way
 * to see that flag. Offering the option regardless put "opens are not recorded
 * for this email" on screen for a mail that records them (2026-09-18 audit).
 *
 * So the option is tied to a build flag of our own, set in `.env.production`
 * only AFTER the consumer flag is live in production. That also makes the
 * deploy order forgiving: this frontend can ship at any time.
 *
 * A template that already HOLDS `clicks` still shows it (see GeneralBody):
 * hiding the value a row carries would be a lie of its own.
 */
export function isClicksModeOffered(): boolean {
  return import.meta.env.VITE_EMAIL_TRACKING_CLICKS_ENABLED === "true";
}

